import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function DeckingPage() {
  const deckMaterials = [
    {
      name: 'Composite Decking',
      description: 'Low-maintenance, durable, and eco-friendly option that resists fading, staining, and scratching.',
      features: ['25+ year lifespan', 'No staining required', 'Splinter-free surface']
    },
    {
      name: 'Pressure-Treated Wood',
      description: 'Traditional and cost-effective choice that offers natural beauty and can be customized with stains.',
      features: ['Budget-friendly', 'Natural wood grain', 'Easy to repair']
    },
    {
      name: 'Cedar & Redwood',
      description: 'Premium natural wood options with natural resistance to rot, decay, and insect damage.',
      features: ['Beautiful natural color', 'Naturally resistant', 'Aromatic properties']
    }
  ];

  const benefits = [
    {
      icon: 'ri-home-smile-line',
      title: 'Expanded Living Space',
      description: 'Create an outdoor oasis perfect for entertaining, relaxing, and enjoying nature.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Increased Home Value',
      description: 'Quality deck construction can provide up to 80% return on investment when selling.'
    },
    {
      icon: 'ri-tools-line',
      title: 'Custom Design Options',
      description: 'From multi-level decks to built-in seating, we bring your vision to life.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Built to Last',
      description: 'Expert craftsmanship ensures your deck withstands weather and heavy use for decades.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Custom Deck Construction
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Transform your outdoor space with a beautifully crafted deck. From intimate backyard retreats to expansive entertainment areas, we design and build custom decks that enhance your lifestyle and increase your home&apos;s value.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="px-8 py-4 bg-brand text-white rounded-lg font-semibold hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Free Estimate
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Deck Materials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Deck Material Options</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose from a variety of premium decking materials to match your style, budget, and maintenance preferences.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {deckMaterials.map((material, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-shadow">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{material.name}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{material.description}</p>
                <ul className="space-y-3">
                  {material.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-brand text-xl mr-2 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Add a Deck?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A professionally built deck offers numerous benefits for your home and lifestyle.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-gradient-to-br from-brand to-brand-dark rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative" style={{ backgroundImage: 'url(https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/c4da62beec0664aa8fffd0b902246009.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-black/55"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Build Your Dream Deck?
          </h2>
          <p className="text-xl text-amber-50 mb-8 leading-relaxed">
            Let&apos;s discuss your vision and create an outdoor space you&apos;ll love for years to come. Get your free consultation and estimate today.
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              window.REACT_APP_NAVIGATE('/');
              setTimeout(() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            className="inline-block px-10 py-4 bg-white text-brand rounded-lg font-bold text-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Estimate
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
